/**
 * Client-side persistent image cache.
 *
 * Why this exists:
 *   The brand logo + background image are served by the FastAPI backend with
 *   `Cache-Control: public, max-age=86400, immutable` AND ETag-based 304
 *   short-circuiting. However, the production Kubernetes / Cloudflare
 *   ingress **rewrites** that header to `no-store, no-cache, must-revalidate`
 *   before it reaches the browser — so every page reload re-fetches the
 *   same 200 KB+ image even though it hasn't changed.
 *
 * Fix:
 *   Cache the image bytes in `localStorage` keyed by the asset URL. The URL
 *   contains a UUID stamped at upload time, so when the SuperAdmin uploads
 *   a fresh image the URL changes and the new entry replaces the old one
 *   naturally. localStorage caps out at ~5 MB per origin which is plenty
 *   for a logo + background pair.
 *
 * Reading is fully synchronous (one `localStorage.getItem` per render, which
 * is microsecond-cheap) so warm-cached visits resolve the data URL on the
 * first render — no flash, no network request.
 *
 * Usage:
 *   const cachedSrc = useCachedImage(url);
 *   <img src={cachedSrc || url} />
 *
 *   `cachedSrc` is null on cache miss; the consumer falls back to the raw
 *   URL which kicks off a single network fetch. Once the bytes are saved,
 *   the hook re-renders and `cachedSrc` becomes the persistent data URL.
 */

import { useEffect, useState } from 'react';

const PREFIX = 'app.imgcache.';
// Hard ceiling per image — 1.5 MB raw → ~2 MB base64. Beyond this we skip
// caching to avoid hitting the localStorage quota with a single asset.
const MAX_RAW_BYTES = 1_500_000;

function readCache(url) {
  try {
    return localStorage.getItem(PREFIX + url) || null;
  } catch {
    return null;
  }
}

function writeCache(url, dataUrl) {
  try {
    localStorage.setItem(PREFIX + url, dataUrl);
  } catch {
    // Quota exceeded — drop our oldest entries and retry once.
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(PREFIX))
        .slice(0, 4)
        .forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(PREFIX + url, dataUrl);
    } catch {
      /* give up silently */
    }
  }
}

export function useCachedImage(url) {
  // Synchronous read on every render. On a warm cache this resolves to the
  // data URL during the very first render — the consumer never falls back
  // to the raw URL, so the browser never opens a connection.
  const cached = url ? readCache(url) : null;

  // Background populator. The dummy state object is only used to force a
  // re-render after the cache is populated so the synchronous read above
  // picks up the new value.
  const [, forceUpdate] = useState({});
  useEffect(() => {
    if (!url || readCache(url)) return undefined; // miss the fetch on warm hits.

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) return;
        const blob = await res.blob();
        if (blob.size > MAX_RAW_BYTES) return; // too big to cache safely.
        const reader = new FileReader();
        reader.onload = () => {
          if (cancelled) return;
          const dataUrl = String(reader.result || '');
          if (!dataUrl.startsWith('data:')) return;
          writeCache(url, dataUrl);
          forceUpdate({});
        };
        reader.readAsDataURL(blob);
      } catch {
        /* network error — caller's fallback to the raw URL handles it. */
      }
    })();

    return () => { cancelled = true; };
  }, [url]);

  return cached;
}
